import { useSyncExternalStore } from "react";

import type { Entry, EntryIn, Food, Schemas } from "../api/client";
import { useMe } from "../lib/auth";
import { scale } from "../lib/format";
import { getStatus, subscribeStatus, syncNow } from "./engine";
import { enqueue, getSnapshot, subscribe } from "./store";
import type { Op } from "./types";

function nowIso(): string {
  return new Date().toISOString().replace("Z", "");
}

/** What the entry will look like once the server has it, computed on the device. */
export function previewEntry(id: string, body: EntryIn, food?: Food | null, existing?: Entry): Entry {
  let n: Record<string, number | null>;
  let name: string;
  let amount: string | null;
  if (food && body.food_id != null) {
    let factor: number;
    if (body.grams != null) {
      factor = body.grams / (food.serving_grams ?? 1);
      amount = `${body.grams} g`;
    } else {
      const serving = food.servings.find((s) => s.id === body.serving_id);
      factor = (body.servings ?? 1) * (serving?.multiplier ?? 1);
      amount = `${body.servings ?? 1} × ${serving?.name ?? food.serving_name}`;
    }
    name = body.name || (food.brand ? `${food.name} (${food.brand})` : food.name);
    n = {
      kcal: scale(food.kcal, factor),
      protein_g: scale(food.protein_g, factor),
      carbs_g: scale(food.carbs_g, factor),
      fat_g: scale(food.fat_g, factor),
      fiber_g: scale(food.fiber_g, factor),
      sugar_g: scale(food.sugar_g, factor),
      saturated_fat_g: scale(food.saturated_fat_g, factor),
      sodium_mg: scale(food.sodium_mg, factor),
    };
  } else {
    name = body.name ?? "";
    amount = body.amount ?? null;
    n = {
      kcal: body.kcal ?? 0,
      protein_g: body.protein_g ?? null,
      carbs_g: body.carbs_g ?? null,
      fat_g: body.fat_g ?? null,
      fiber_g: body.fiber_g ?? null,
      sugar_g: body.sugar_g ?? null,
      saturated_fat_g: body.saturated_fat_g ?? null,
      sodium_mg: body.sodium_mg ?? null,
    };
  }
  return {
    id,
    date: body.date,
    meal: body.meal,
    name,
    food_id: body.food_id ?? null,
    servings: body.servings ?? null,
    serving_id: body.serving_id ?? null,
    grams: body.grams ?? null,
    amount,
    notes: body.notes ?? null,
    kcal: n.kcal ?? 0,
    protein_g: n.protein_g ?? null,
    carbs_g: n.carbs_g ?? null,
    fat_g: n.fat_g ?? null,
    fiber_g: n.fiber_g ?? null,
    sugar_g: n.sugar_g ?? null,
    saturated_fat_g: n.saturated_fat_g ?? null,
    sodium_mg: n.sodium_mg ?? null,
    created_at: existing?.created_at ?? nowIso(),
    updated_at: nowIso(),
  };
}

/** The request that recreates an entry (e.g. for Undo). */
export function entryBody(e: Entry): EntryIn {
  const base = { date: e.date, meal: e.meal, notes: e.notes };
  if (e.food_id != null) {
    return e.grams != null
      ? { ...base, food_id: e.food_id, grams: e.grams }
      : { ...base, food_id: e.food_id, servings: e.servings ?? 1, serving_id: e.serving_id };
  }
  return {
    ...base,
    name: e.name,
    amount: e.amount,
    kcal: e.kcal,
    protein_g: e.protein_g,
    carbs_g: e.carbs_g,
    fat_g: e.fat_g,
    fiber_g: e.fiber_g,
    sugar_g: e.sugar_g,
    saturated_fat_g: e.saturated_fat_g,
    sodium_mg: e.sodium_mg,
  };
}

/**
 * Changes to the food log. Each is saved on the device first and sent when possible, so
 * they work offline and are never lost to a dropped connection.
 */
export function useLogActions() {
  const userId = useMe().data?.id;
  const queue = async (op: Op) => {
    if (userId === undefined) throw new Error("Not signed in");
    await enqueue(userId, op);
    void syncNow();
  };
  return {
    putEntry: (id: string, body: EntryIn, preview: Entry) => queue({ kind: "putEntry", id, body, preview }),
    deleteEntry: (e: Entry) => queue({ kind: "deleteEntry", id: e.id, date: e.date, name: e.name }),
    restoreEntry: (e: Entry) => queue({ kind: "putEntry", id: e.id, body: entryBody(e), preview: e }),
    addWater: (date: string, delta: number) =>
      queue({ kind: "increment", key: crypto.randomUUID(), body: { date, metric: "water_ml", delta } }),
    /** Weight in kg, or null to remove the day's manual weigh-in. */
    setWeight: (date: string, kg: number | null) =>
      queue({ kind: "setMetric", date, metric: "weight_kg", value: kg == null ? null : Math.round(kg * 100) / 100 }),
    createFoodAndLog: (food: Schemas["FoodCreate"], entryId: string, entry: Omit<EntryIn, "food_id">, preview: Entry) =>
      queue({ kind: "createFoodAndLog", foodKey: crypto.randomUUID(), food, entryId, entry, preview }),
  };
}

/** Pending/failed changes for the signed-in user, and the connection status. */
export function useSyncState() {
  const userId = useMe().data?.id;
  const snap = useSyncExternalStore(subscribe, getSnapshot);
  const status = useSyncExternalStore(subscribeStatus, getStatus);
  const mine = <T extends { userId: number }>(xs: T[]) => xs.filter((o) => o.userId === userId);
  return {
    pending: mine(snap.pending),
    failed: mine(snap.failed),
    settled: mine(snap.settled),
    persistent: snap.persistent,
    status,
  };
}
